/**
 * User property management for analytics
 * Matches iOS AnalyticsUserProperties.swift exactly
 */

import { getSelectedStoreId, getUserId, getUserStoreId } from '../../src/utils'
import { UserLoginData } from '../services/user'

/**
 * User properties that are set once per user (not events)
 * These properties help segment users in analytics
 */
export const AnalyticsUserProperties = {
  userId: 'user_id',
  platform: 'platform',
  signupDate: 'signup_date',
  subscriptionStatus: 'subscription_status',
  subscriptionPlan: 'subscription_plan',
  userType: 'user_type',
  storeId: 'store_id',
  hasGstin: 'hasgstin',
  hasLogo: 'haslogo',
  hasSigns: 'hassigns',
  country: 'country',
  language: 'language',
  isStaffUser: 'is_staff_user',
  userLevel: 'user_level',
} as const

/**
 * Helper to get user property values from User model
 * Matches iOS UserPropertyHelper exactly
 */
export class UserPropertyHelper {
  /**
   * Get subscription status from user data
   */
  static getSubscriptionStatus(user: UserLoginData | null): string {
    if (!user) return 'none'

    if (user.subscription) {
      const endDate = user.subscription.endDate
      if (endDate) {
        const endDateValue = new Date(endDate)
        if (endDateValue > new Date()) {
          return 'active'
        } else {
          return 'cancelled'
        }
      }
    }

    if (user.activeSubscriptionId) {
      return 'active'
    }

    return 'none'
  }

  /**
   * Get subscription plan from user data
   */
  static getSubscriptionPlan(user: UserLoginData | null): string {
    if (!user) return 'none'

    if (user.planName) {
      const lowercased = user.planName.toLowerCase()
      if (lowercased.includes('monthly') || lowercased.includes('month')) {
        return 'monthly'
      } else if (lowercased.includes('yearly') || lowercased.includes('year')) {
        return 'yearly'
      }
    }

    return 'none'
  }

  /**
   * Get user type from user data
   */
  static getUserType(user: UserLoginData | null): string {
    if (!user) return 'unknown'
    return user.userType?.toLowerCase() || 'unknown'
  }

  /**
   * Get store ID from user data
   */
  static getStoreId(): string | null {
    return getSelectedStoreId() ?? getUserStoreId()
  }

  static getCurrentUserId(): string | null {
    return getUserId()
  }

  /**
   * Check if user has GSTIN
   */
  static hasGstin(user: UserLoginData | null): boolean {
    if (!user || !user.store?.gstin) return false
    return user.store.gstin.length > 0
  }

  /**
   * Check if user has logo
   */
  static hasLogo(user: UserLoginData | null): boolean {
    if (!user || !user.store?.signUrl) return false
    return user.store.signUrl.length > 0
  }

  /**
   * Check if user has signs enabled
   */
  static hasSigns(user: UserLoginData | null): boolean {
    if (!user) return false
    // Assuming signingEnabled is available in store, adjust based on actual UserLoginData structure
    return user.store?.signingEnabled === true
  }
}
